import { Card, ComboboxData, ComboboxItem, Grid, Loader, Select, Title } from "@mantine/core";
import { issuePriortyEnums, TIssueStatus } from "@mctypes/global.types";
import { IIssueBoardApi } from "@schemas/issueBoardSchema";
import { IIssueApi, IIssueUpdateSchemaProps, IIssueUpdateUserSchemaProps, IUserPartial } from "@schemas/issueSchema";
import { IconExternalLink } from "@tabler/icons-react";
import React, { useCallback, useMemo } from "react";
import {
  usePutProjectByProjectIdIssueUpdateSimpleMutation,
  usePutProjectByProjectIdIssueUpdateUserMutation,
} from "../../../../../../../openapi";
import { McTitle } from "../../../../../../components";
import FoldableSection from "../../../../../../components/FoldableSection/FoldableSection";
import { McAutocomplete } from "../../../../../../components/McAutocomplete";
import { McColorSelect } from "../../../../../../components/McColorSelect";
import McInputNew from "../../../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../../../components/McInput/McTextAreaNew";
import { McMantineMultiUserSelect } from "../../../../../../components/McMantineUserSelect/McMantineUserMultiSelect";
import { McMantineUserSelect } from "../../../../../../components/McMantineUserSelect/McMantineUserSelect";
import { SimpleTagsMantine } from "../../../../../../components/SimpleTags/SimpleTagsMantine";
import { useAppSelector } from "../../../../../../hooks";
import { IssueDisabledColumnsEnum } from "../../../../../../mc-shared/enums";
import { formatTags } from "../../../../../utils";
import { ISSUE_PRIORTY_OPTIONS, ISSUE_STATES_OPTIONS } from "../../../issueConstants";
import { useGetUniqueTagsOnBoardQuery } from "../../../issueRTK";
import { deduplicatedMembers, selectIssueBoardBuckets, selectTypeAheadPNSItems } from "../../../issueSelectors";
import { IssueConnectedBimSpacesSection } from "./IssueConnectedBimSpacesSection";
import { CellCtx, CellDef, createSimplePayload, createUserPayload, guardedIssueUpdate } from "./modalUtils";

const CELL_DEFS: CellDef[] = [
  {
    id: "owner",
    disableKey: IssueDisabledColumnsEnum.OWNER,
    span: 4,
    render: (ctx) => (
      <McTitle title="Eier">
        <McMantineUserSelect
          value={ctx.issue.owner?._id}
          data={ctx.usersComboboxData}
          onChange={(v) =>
            ctx.updateUser({ owner: v == null ? null : ctx.multiUsersComboboxData.find((u) => u._id === v) })
          }
        />
      </McTitle>
    ),
  },
  {
    id: "assignedTo",
    disableKey: IssueDisabledColumnsEnum.ASSIGNEDTO,
    span: 4,
    render: (ctx) => (
      <McTitle title="Ansv. for utførelse">
        <McMantineUserSelect
          value={ctx.issue.assignedTo?._id}
          data={ctx.usersComboboxData}
          onChange={(v) =>
            ctx.updateUser({ assignedTo: v == null ? null : ctx.multiUsersComboboxData.find((u) => u._id === v) })
          }
        />
      </McTitle>
    ),
  },
  {
    id: "members",
    disableKey: IssueDisabledColumnsEnum.ITEMMEMBERS,
    span: 4,
    render: (ctx) => (
      <McTitle title="Medlemmer">
        <McMantineMultiUserSelect
          defaultValue={ctx.issue.itemMembers || []}
          data={ctx.multiUsersComboboxData}
          onChange={(v) => ctx.updateUser({ itemMembers: v })}
        />
      </McTitle>
    ),
  },
  {
    id: "status",
    disableKey: IssueDisabledColumnsEnum.STATUS,
    span: 4,
    render: (ctx) => (
      <McTitle title="Status">
        <McColorSelect
          onChange={(newValue) => ctx.updateSimple({ status: newValue as TIssueStatus })}
          data={ISSUE_STATES_OPTIONS.map((s) => ({
            key: s.value,
            displayName: s.value,
            color: s.color,
          }))}
          value={ctx.issue.status}
        />
      </McTitle>
    ),
  },
  {
    id: "priority",
    disableKey: IssueDisabledColumnsEnum.PRIORITY,
    span: 4,
    render: (ctx) => (
      <McTitle title="Prioritet">
        <McColorSelect
          onChange={(newValue) => ctx.updateSimple({ priority: newValue as issuePriortyEnums })}
          data={ISSUE_PRIORTY_OPTIONS}
          value={ctx.issue.priority}
        />
      </McTitle>
    ),
  },
  {
    id: "referenceUrl",
    disableKey: IssueDisabledColumnsEnum.REFERENCEURL,
    span: 4,
    render: (ctx) => (
      <McTitle title="Link">
        <McTitle.InlineData positionInlineData="flex-end">
          {/https?:\/\/.*/i.test(ctx.issue.referenceUrl) === true && (
            <a
              style={{ display: "flex", color: "var(--mantine-color-blue-4)" }}
              href={ctx.issue.referenceUrl}
              target="_blank"
              rel="noreferrer"
            >
              <IconExternalLink size={16} />
            </a>
          )}
        </McTitle.InlineData>

        <McInputNew
          placeholder="http://"
          onBlur={(e) => ctx.updateSimple({ referenceUrl: e.target.value })}
          value={ctx.issue.referenceUrl}
        />
      </McTitle>
    ),
  },
  {
    id: "bucket",
    disableKey: IssueDisabledColumnsEnum.BUCKET,
    span: 4,
    render: (ctx) => (
      <McTitle title={ctx.issueBoard?.bucketNameSetting || ""}>
        <Select
          allowDeselect={false}
          value={ctx.issue.bucket}
          data={ctx.bucketOptions}
          onChange={(newValue) => ctx.updateSimple({ bucket: newValue })}
          disabled={ctx.issue.taskOrIssue === "TASK"}
        />
      </McTitle>
    ),
  },
  {
    id: "typeOfIssue",
    disableKey: IssueDisabledColumnsEnum.TYPEOFISSUE,
    span: 4,
    render: (ctx) => (
      <McTitle title="Type">
        <Select
          allowDeselect={false}
          value={ctx.issue.typeOfIssue}
          data={ctx.typeOptions}
          onChange={(newValue) => ctx.updateSimple({ typeOfIssue: newValue })}
        />
      </McTitle>
    ),
  },
  {
    id: "pns",
    disableKey: IssueDisabledColumnsEnum.PNS,
    span: 4,
    render: (ctx) => (
      <McTitle title="PNS">
        <McAutocomplete
          clearable={true}
          onClear={() => ctx.updateSimple({ pns: "" })}
          data={ctx.typeAheadPNSItems}
          value={ctx.issue.pns}
          onCommit={(e) => ctx.updateSimple({ pns: e })}
        />
      </McTitle>
    ),
  },
  {
    id: "description",
    disableKey: IssueDisabledColumnsEnum.DESCRIPTION,
    span: 12,
    render: (ctx) => (
      <McTitle title="Beskrivelse">
        <McTextAreaNew
          value={ctx.issue.description}
          onBlur={(e) => ctx.updateSimple({ description: e.target.value })}
          autosize
        />
      </McTitle>
    ),
  },
  {
    id: "conclusion",
    disableKey: IssueDisabledColumnsEnum.CONCLUSION,
    span: 12,
    when: (ctx) =>
      (ctx.issue.status === "Fullført" && ctx.issueBoard.showConclusion === true) ||
      ctx.issueBoard.showConclusion === false,
    render: (ctx) => (
      <McTitle title="Konklusjon">
        <McTextAreaNew
          value={ctx.issue.conclusion}
          onBlur={(e) => ctx.updateSimple({ conclusion: e.target.value })}
          autosize
        />
      </McTitle>
    ),
  },
  {
    id: "tags",
    span: 12,
    render: (ctx) => (
      <McTitle title="Etiketter">
        <SimpleTagsMantine
          activeTags={ctx.issue.tags}
          tagOptions={ctx.issueTags}
          allowNew={ctx.issueBoard.useOnlyPredefinedTags !== true}
          onChange={async (value) => await ctx.updateSimple({ tags: value })}
        />
      </McTitle>
    ),
  },
  {
    id: "connectedBimSpaces",
    disableKey: IssueDisabledColumnsEnum.CONNECTEDBIMSPACES,
    span: 12,
    render: (ctx) => <IssueConnectedBimSpacesSection issue={ctx.issue} projectId={ctx.projectId} />,
  },
];

export const IssueMainPropertiesSection: React.FC<{
  issue: IIssueApi;
  issueBoard?: IIssueBoardApi;
  projectId: string;
}> = ({ issue, issueBoard, projectId }) => {
  if (issue == null || issueBoard == null) {
    return <Loader />;
  }

  const [updateValue] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const [updateUserValue] = usePutProjectByProjectIdIssueUpdateUserMutation();
  const buildSimplePayload = useMemo(() => createSimplePayload(projectId, issue._id), [projectId, issue._id]);
  const buildUserPayload = useMemo(() => createUserPayload(projectId, issue._id), [projectId, issue._id]);

  const updateSimple = useCallback(
    async (prop: IIssueUpdateSchemaProps) => {
      await guardedIssueUpdate({
        issue,
        prop,
        mutation: updateValue,
        buildPayload: buildSimplePayload,
      });
    },
    [issue, updateValue, buildSimplePayload],
  );

  const updateUser = useCallback(
    async (prop: IIssueUpdateUserSchemaProps) => {
      await guardedIssueUpdate({
        issue,
        prop,
        mutation: updateUserValue,
        buildPayload: buildUserPayload,
      });
    },
    [issue, updateUserValue, buildUserPayload],
  );

  const issueBoardBuckets = useAppSelector(selectIssueBoardBuckets);
  const typeAheadPNSItems = useAppSelector(selectTypeAheadPNSItems);
  const projectUsers = useAppSelector(deduplicatedMembers);

  // Remove and use multiUsersComboboxData when custom SingleUserSelect is created
  const usersComboboxData: ComboboxItem[] = useMemo(
    () =>
      projectUsers.map((u) => ({
        label: u.user.name,
        value: u.user._id,
      })),
    [projectUsers],
  );

  const multiUsersComboboxData: IUserPartial[] = useMemo(
    () =>
      projectUsers.map((u) => ({
        _id: u.user._id,
        name: u.user.name,
      })),
    [projectUsers],
  );

  const bucketOptions: ComboboxData = useMemo(
    () => issueBoardBuckets.map((b) => ({ value: b.value, label: b.value })),
    [issueBoardBuckets],
  );

  const typeOptions: ComboboxData = useMemo(
    () => (issueBoard?.types ?? []).map((t) => ({ value: t.name, label: t.name })),
    [issueBoard?.types],
  );

  const { data: tags, isLoading: tagsLoading } = useGetUniqueTagsOnBoardQuery({
    projectId: projectId,
    boardId: issueBoard._id,
  });

  const issueTags = formatTags(tags);
  const disabledSet = useMemo(() => new Set(issueBoard?.disabledColumns ?? []), [issueBoard?.disabledColumns]);

  const ctx: CellCtx = useMemo(
    () => ({
      issue,
      issueBoard,
      projectId,
      usersComboboxData,
      multiUsersComboboxData,
      bucketOptions,
      typeOptions,
      typeAheadPNSItems,
      issueTags,
      tagsLoading,
      updateSimple,
      updateUser,
    }),
    [
      issue,
      issueBoard,
      projectId,
      usersComboboxData,
      multiUsersComboboxData,
      bucketOptions,
      typeOptions,
      typeAheadPNSItems,
      issueTags,
      tagsLoading,
      updateSimple,
      updateUser,
    ],
  );

  const visibleCells = useMemo(() => {
    return CELL_DEFS.filter((cell) => {
      // if the cell is disableable, check it
      if (cell.disableKey != null && disabledSet.has(cell.disableKey)) return false;
      return true;
    }).filter((cell) => (cell.when ? cell.when(ctx) : true));
  }, [disabledSet, ctx]);

  return (
    <FoldableSection storageKey="issue-modal-properties">
      <FoldableSection.Header>
        <Title order={6}>Detaljer</Title>
      </FoldableSection.Header>

      <FoldableSection.Body>
        <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
          <Grid flex={1}>
            {visibleCells.map((cell) => (
              <Grid.Col key={cell.id} span={typeof cell.span === "number" ? cell.span : cell.span(ctx)}>
                {cell.render(ctx)}
              </Grid.Col>
            ))}
          </Grid>
        </Card>
      </FoldableSection.Body>
    </FoldableSection>
  );
};
